import React from "react";

// ponytail: tiny markdown for change summaries only (headings, lists, bold/italic/code).
// Upgrade to react-markdown if summaries need tables/links/nested lists.
const MARKDOWN_HINT =
  /(^|\n)\s{0,3}#{1,6}\s|(^|\n)\s*[-*+]\s|\*\*[^*]+\*\*|__[^_]+__|(^|\n)\s*\d+\.\s|`[^`]+`/;

const looksLikeMarkdown = (text) => MARKDOWN_HINT.test(text);

const renderInline = (str) => {
  const nodes = [];
  const re = /\*\*(.+?)\*\*|__(.+?)__|\*(.+?)\*|_(.+?)_|`(.+?)`/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = re.exec(str))) {
    if (match.index > last) nodes.push(str.slice(last, match.index));
    if (match[1] != null || match[2] != null) {
      nodes.push(<strong key={key++}>{match[1] ?? match[2]}</strong>);
    } else if (match[3] != null || match[4] != null) {
      nodes.push(<em key={key++}>{match[3] ?? match[4]}</em>);
    } else {
      nodes.push(
        <code key={key++} className="rounded bg-theme-secondary/40 px-1 text-[0.9em]">
          {match[5]}
        </code>,
      );
    }
    last = match.index + match[0].length;
  }
  if (last < str.length) nodes.push(str.slice(last));
  return nodes;
};

const headingClass = (level) =>
  level <= 2 ? "mb-1 text-base font-semibold" : "mb-1 font-semibold";

const renderMarkdown = (text) => {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = Math.min(heading[1].length, 4);
      const Tag = `h${level}`;
      blocks.push(
        <Tag key={key++} className={headingClass(level)}>
          {renderInline(heading[2])}
        </Tag>,
      );
      i += 1;
      continue;
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(
          <li key={items.length}>{renderInline(lines[i].replace(/^\s*[-*+]\s+/, ""))}</li>,
        );
        i += 1;
      }
      blocks.push(
        <ul key={key++} className="mb-1 list-disc space-y-0.5 pl-4 last:mb-0">
          {items}
        </ul>,
      );
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(
          <li key={items.length}>{renderInline(lines[i].replace(/^\s*\d+\.\s+/, ""))}</li>,
        );
        i += 1;
      }
      blocks.push(
        <ol key={key++} className="mb-1 list-decimal space-y-0.5 pl-4 last:mb-0">
          {items}
        </ol>,
      );
      continue;
    }

    const para = [];
    while (
      i < lines.length
      && lines[i].trim()
      && !/^(#{1,6})\s+/.test(lines[i])
      && !/^\s*[-*+]\s+/.test(lines[i])
      && !/^\s*\d+\.\s+/.test(lines[i])
    ) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={key++} className="mb-1 last:mb-0">
        {renderInline(para.join(" "))}
      </p>,
    );
  }

  return blocks;
};

const SummaryText = ({ text, className = "" }) => {
  const summary = (text || "").trim();
  if (!summary) return null;

  if (!looksLikeMarkdown(summary)) {
    return <div className={`whitespace-pre-wrap ${className}`.trim()}>{summary}</div>;
  }

  return <div className={className}>{renderMarkdown(summary)}</div>;
};

export { looksLikeMarkdown, SummaryText };
export default SummaryText;
